import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trash2, Music } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const audioTypeLabels = {
  // Indigenous & Cultural
  'indigenous_elders': 'Indigenous Elders & Knowledge Holders',
  'cultural_custodians': 'Cultural Custodians',
  // Scientific & Research
  'ecologists': 'Ecologists',
  'botanists': 'Botanists',
  'mycorrhizal': 'Mycorrhizal Researchers',
  'climate_scientists': 'Climate Scientists',
  'fire_ecologists': 'Fire Ecologists',
  'hydrologists': 'Hydrologists',
  // Practitioners & Land Workers
  'arborists': 'Arborists',
  'rangers': 'Rangers',
  'land_managers': 'Land Managers',
  'timber_workers': 'Timber Workers',
  'agroforesters': 'Agroforesters',
  // Community & Humanities
  'historians': 'Historians',
  'local_community': 'Local Community',
  'children': 'Children',
  'artists': 'Artists',
  'faith_leaders': 'Faith Leaders',
  'philosophers': 'Philosophers',
  'poets': 'Poets',
  // Policy & Advocacy
  'planners': 'Planners',
  'lawyers': 'Lawyers',
  'rights_advocates': 'Rights-of-Nature Advocates',
};

const ManageAudioModal = ({ tree, onClose, onSuccess }) => {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (audioType) => {
    if (!window.confirm(`Are you sure you want to delete the ${audioTypeLabels[audioType]} audio track?`)) {
      return;
    }

    setDeleting(audioType);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/trees/${tree.id}/audio/${audioType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Audio track deleted successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error deleting audio track:', error);
      toast.error('Failed to delete audio track');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="manage-audio-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Music className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-bold text-primary">Manage Audio Tracks</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Tree:</p>
            <p className="font-medium text-primary">{tree.name}</p>
          </div>

          {tree.audio_tracks && tree.audio_tracks.length > 0 ? (
            <div className="space-y-3">
              {tree.audio_tracks.map((track, index) => (
                <div
                  key={`${track.type}-${index}`}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary/30 transition-colors"
                  data-testid={`audio-track-item-${track.type}`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="font-medium text-primary mb-1">
                      {audioTypeLabels[track.type] || track.type}
                    </h3>
                    {track.title && (
                      <p className="text-sm text-muted-foreground mb-1">{track.title}</p>
                    )}
                    <p className="text-xs text-muted-foreground truncate">
                      {track.url}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(track.type)}
                    disabled={deleting === track.type}
                    className="flex items-center gap-2 px-4 py-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors disabled:opacity-50"
                    data-testid={`delete-audio-${track.type}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    {deleting === track.type ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-audio-tracks">
              <Music className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No audio tracks yet.</p>
              <p className="text-sm mt-2">Add audio tracks using the music icon in the dashboard.</p>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-secondary transition-colors"
              data-testid="close-button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAudioModal;
